import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { jwtDecode } from 'jwt-decode';
import { loginParam, reponseObject } from '../../models/response.model';

@Component({
  selector: 'app-login',
  templateUrl: `./login.component.html`,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  timeLeft: number = 900;
  interval!: ReturnType<typeof setInterval>;
  subscribeTimer: string = '';
  sessionExpiredMessage: string | null = null;

  loginForm = new FormGroup({
    userName: new FormControl(''),
    passcode: new FormControl(''),
  });

  forgetPasswordForm = new FormGroup({
    userName: new FormControl(),
    passcode: new FormControl(),
  });

  validateToken: string = '';
  showPasscode: boolean = false;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    // Subscribe to session expiration event
    this.authService.sessionExpired$.subscribe(() => {
      this.showSessionExpiredMessage();
    });
  }

  // Show session expired message
  showSessionExpiredMessage() {
    this._snackBar.open('Session expired. Please log in again.', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });

    // Redirect to login after the snackbar is shown
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 3000); // Wait for the snackbar to disappear
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['message']) {
        this.sessionExpiredMessage = params['message'];
      }
    });
  }

  loginHere() {
    const data = this.loginForm.controls['userName'].value ?? '';
    this.authService.auth(data).subscribe(
      (response: reponseObject) => {
        if (!response.isError) {
          this.validateToken = response.value.token;
          this.timer();
          this.showPasscode = !this.showPasscode;
        } else {
          this._snackBar.open(response.value.value, 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      },
      (error) => {
        this._snackBar.open(
          'Please verify your Email or Username, does not match our records.',
          'Close',
          {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          },
        );
        // Handle error scenario if needed
      },
    );
  }

  timer() {
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        this.timeLeft = 60;
      }
    }, 1000);
  }

  validateLoginToken() {
    const input: loginParam = {
      resetToken: this.validateToken,
      passCode: this.loginForm.controls['passcode'].value ?? '',
    };
    this.authService.validateLogin(input).subscribe(
      (response) => {
        const token = response.value; // Adjust according to
        this.isTokenExpired(token);
        if (response.isError) {
          this._snackBar.open(
            'Passcode is incorrect, please enter valid passcode',
            'Close',
            {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            },
          );
        } else {
          // Redirect to the dashboard's parent route where the SideNavComponent is loaded
          if (token !== null) {
            sessionStorage.setItem('authToken', token);
            this.authService.setToken(token); // Store the token
            this.isTokenExpired(token);
            sessionStorage.setItem('authToken', token);
            this.authService.setToken(token); // Store the token
            this.authService.notifyAuthState(true); // Notify authentication state change
            this.router.navigate(['/klusup/accounting/account']);
            this._snackBar.open('Login Successful', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          } else {
            this._snackBar.open('Invalid Token', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          }
        }
      },
      () => {
        this._snackBar.open('Something went wrong please again later', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    );
  }

  // Check if the token is expired
  isTokenExpired(token: string): boolean {
    try {
      const decodedToken: any = token ? jwtDecode(token) : null;
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      sessionStorage.setItem('userName', decodedToken.sub);
      sessionStorage.setItem('userId', decodedToken.userId);
      return decodedToken.exp < currentTime;
    } catch (error) {
      // If token is invalid or can't be decoded, consider it expired
      return true;
    }
  }

  //  State to toggle the right-panel-active class
  isRightPanelActive = false;

  ngOnDestroy() {
    clearInterval(this.interval);
  }
}
